// The installable chat app ("IRG Chat"): a chat-only, full-screen shell at /chat with its own manifest
// (public/chat-manifest.json). The ERP itself is no longer installable; its install prompts send people
// here instead. public/index.html links the manifest on /chat page loads; keep the path test in sync.
import { useEffect } from "react";
import { isRunningStandalone } from "./pwa";

export const CHAT_APP_PATH = "/chat";
const MANIFEST_HREF = `${process.env.PUBLIC_URL}/chat-manifest.json`;
const LAUNCHED_KEY = "irg-chat-app-window";

export const isChatAppPath = (pathname = window.location.pathname) => /^\/chat(\/|$)/.test(pathname);

// Remember, for this window, that it was launched as the chat app. A link out of the chat app keeps the
// standalone window, and that must not be mistaken for the old, discontinued ERP app below.
try {
  if (isRunningStandalone() && isChatAppPath()) sessionStorage.setItem(LAUNCHED_KEY, "1");
} catch {
  /* storage blocked: the check below then only looks at the path */
}

// Running as the old installed ERP app (manifest.json, scope "/"), which has been discontinued.
export const isLegacyErpApp = (pathname = window.location.pathname) => {
  if (!isRunningStandalone() || isChatAppPath(pathname)) return false;
  try {
    return sessionStorage.getItem(LAUNCHED_KEY) !== "1";
  } catch {
    return true;
  }
};

// Send people to the chat app page, where the browser offers to install it. A full page load so the
// browser reads the chat manifest from the start.
export const goInstallChatApp = () => window.location.assign(`${CHAT_APP_PATH}?install=1`);

// Absolute link to the chat app, for copying into another browser.
export const chatAppUrl = () => `${window.location.origin}${CHAT_APP_PATH}`;

// Arriving at /chat through in-app navigation (no page load): link the manifest so the page is
// installable, and drop it again on the way out so ERP pages stay non-installable.
export function useChatManifest() {
  useEffect(() => {
    let link = document.querySelector('link[rel="manifest"]');
    if (link) return undefined;
    link = document.createElement("link");
    link.rel = "manifest";
    link.href = MANIFEST_HREF;
    document.head.appendChild(link);
    return () => link.remove();
  }, []);
}
